import React, { useEffect, useMemo, useState } from 'react';
import api, { unwrap } from '../utils/api';
import Icon from '../components/Icon';

// Centralised view of every category's filterOptions. The data lives on the
// Category model (filterOptions: [String]) so this page just patches that
// array via the existing /categories/UpdateCategory endpoint — no backend
// changes needed.
export default function Filters() {
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [err, setErr] = useState('');
  const [draftCategoryId, setDraftCategoryId] = useState('');
  const [draftLabel, setDraftLabel] = useState('');

  async function load() {
    setLoading(true); setErr('');
    try {
      const r = await api.get('/categories/GetCategoriesAndSubCategoriesSimple');
      setCats(unwrap(r) || []);
    } catch (e) {
      setErr(e?.response?.data?.body || e.message || 'Failed to load categories');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  // Patch a category's filterOptions list and reflect the change locally
  // so the UI updates without re-fetching.
  async function patchFilters(catId, nextFilters) {
    setSavingId(catId); setErr('');
    try {
      const r = await api.post('/categories/UpdateCategory', { _id: catId, filterOptions: nextFilters });
      const saved = unwrap(r);
      setCats(prev => prev.map(c => c._id === catId ? { ...c, filterOptions: saved?.filterOptions ?? nextFilters } : c));
    } catch (e) {
      setErr(e?.response?.data?.body || e.message || 'Save failed');
    } finally {
      setSavingId(null);
    }
  }

  async function addFilter() {
    const label = draftLabel.trim();
    if (!draftCategoryId) { setErr('Pick a category'); return; }
    if (!label) { setErr('Type a filter name'); return; }
    const cat = cats.find(c => c._id === draftCategoryId);
    if (!cat) { setErr('Category not found'); return; }
    const current = cat.filterOptions || [];
    if (current.some(f => f.toLowerCase() === label.toLowerCase())) {
      setErr(`"${label}" already exists on ${cat.name}`);
      return;
    }
    await patchFilters(draftCategoryId, [...current, label]);
    setDraftLabel('');
  }

  async function removeFilter(catId, label) {
    if (!window.confirm(`Remove the "${label}" filter? Customers will no longer see it on this category.`)) return;
    const cat = cats.find(c => c._id === catId);
    if (!cat) return;
    const next = (cat.filterOptions || []).filter(f => f !== label);
    await patchFilters(catId, next);
  }

  // For "Parent · Sub" labelling in the dropdown so duplicate sub-category
  // names (e.g. several "Bris" entries) are unambiguous.
  const labelById = useMemo(() => {
    const m = {};
    for (const c of cats) m[c._id] = c.name;
    return m;
  }, [cats]);

  function displayName(c) {
    if (c.parentCategoryId && labelById[c.parentCategoryId]) {
      return `${labelById[c.parentCategoryId]} · ${c.name}`;
    }
    return c.name;
  }

  const sorted = [...cats].sort((a, b) => {
    const aName = displayName(a).toLowerCase();
    const bName = displayName(b).toLowerCase();
    return aName.localeCompare(bName);
  });

  // Only render rows for categories that have at least one filter — keeps
  // the table from being mostly empty for catalog categories that don't
  // use filters at all.
  const withFilters = sorted.filter(c => (c.filterOptions || []).length > 0);

  if (loading) return <><h1>Filters</h1><p>Loading…</p></>;

  return (
    <>
      <h1>Filters</h1>
      <p style={{ color: '#666', marginTop: -6, marginBottom: 18, fontSize: 13 }}>
        These are the checkboxes customers see on a category page. Add a filter to a
        category, and any template tagged with that filter will be discoverable through it.
        Language filters (Hebrew / Yiddish / English) work the same way — they're just
        rendered in a separate group on the customer side.
      </p>

      <div className="card" style={{ marginBottom: 22 }}>
        <h3 style={{ margin: '0 0 12px' }}>Add a filter</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(180px, 240px) 1fr auto', gap: 10, alignItems: 'center' }}>
          <select
            value={draftCategoryId}
            onChange={e => setDraftCategoryId(e.target.value)}
            style={{ padding: 10, border: '1px solid #ddd', borderRadius: 8, font: 'inherit' }}
          >
            <option value="">Select category…</option>
            {sorted.map(c => (
              <option key={c._id} value={c._id}>{displayName(c)}</option>
            ))}
          </select>
          <input
            type="text"
            placeholder="Filter name (e.g. Bris, Hebrew, Pidyon Haben)"
            value={draftLabel}
            onChange={e => setDraftLabel(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') addFilter(); }}
            style={{ padding: 10, border: '1px solid #ddd', borderRadius: 8, font: 'inherit' }}
          />
          <button
            className="btn"
            onClick={addFilter}
            disabled={!draftCategoryId || !draftLabel.trim() || savingId === draftCategoryId}
          >
            {savingId === draftCategoryId ? 'Adding…' : 'Add filter'}
          </button>
        </div>
        {err && <p style={{ color: '#b42318', marginTop: 10, fontSize: 13 }}>{err}</p>}
      </div>

      <h2 style={{ margin: '0 0 12px' }}>All filters by category</h2>
      {withFilters.length === 0 ? (
        <p style={{ color: '#888' }}>No filters configured yet. Add one above.</p>
      ) : (
        withFilters.map(c => (
          <div className="card" key={c._id} style={{ marginBottom: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
              <h3 style={{ margin: 0 }}>{displayName(c)}</h3>
              <span style={{ color: '#888', fontSize: 13 }}>{(c.filterOptions || []).length} filter{(c.filterOptions || []).length === 1 ? '' : 's'}</span>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {(c.filterOptions || []).map(label => (
                <span
                  key={label}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '6px 10px 6px 12px',
                    background: '#f5f7fa',
                    border: '1px solid #e6eaef',
                    borderRadius: 999,
                    fontSize: 13,
                  }}
                >
                  {label}
                  <button
                    type="button"
                    onClick={() => removeFilter(c._id, label)}
                    disabled={savingId === c._id}
                    aria-label={`Remove ${label}`}
                    title={`Remove ${label}`}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#888',
                      fontSize: 16,
                      lineHeight: 1,
                      padding: '0 2px',
                    }}
                  >
                    <Icon name="close" size="16px" />
                  </button>
                </span>
              ))}
            </div>
          </div>
        ))
      )}
    </>
  );
}
